import { Link, Outlet, useLocation } from "react-router";
import {
  BarChart3,
  CalendarCheck2,
  ChevronDown,
  ChevronRight,
  LogOut,
  MapIcon,
  Menu,
} from "lucide-react";
import { useEffect, useState } from "react";
import { cn } from "@/lib/utils";
import { ProtectedRoute } from "@/components/protected-route";
import { DispatchCenterFilter } from "@/components/dispatch-center-filter";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useCarrierMe } from "@/api/carrier";
import { useSession } from "@/services/auth0/session";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Separator } from "@/components/ui/separator";
import {
  getVisibleSidebarGroups,
  isSidebarRouteActive,
  type SidebarChildItem,
  type SidebarItem,
} from "@/screens/layout-nav";

const QUICK_ACCESS_ITEMS = [
  {
    to: "/dispatch",
    label: "Dispatch",
    title: "Go to dispatch",
    Icon: MapIcon,
    desktopLabelClassName: "hidden xl:inline",
    visibleOnNarrowMobile: true,
  },
  {
    to: "/analytics",
    label: "Analytics",
    title: "Go to analytics",
    Icon: BarChart3,
    desktopLabelClassName: "hidden xl:inline",
    visibleOnNarrowMobile: false,
  },
  {
    to: "/pickup-bookings",
    label: "Pickup bookings",
    title: "Go to pickup bookings",
    Icon: CalendarCheck2,
    desktopLabelClassName: "hidden 2xl:inline",
    visibleOnNarrowMobile: false,
  },
] as const;

function isQuickAccessRouteActive(pathname: string, to: string): boolean {
  return pathname === to || pathname.startsWith(`${to}/`);
}

function avatarInitials(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) return "?";
  const parts = trimmed.split(/\s+/).filter(Boolean);
  if (parts.length > 1) {
    return `${parts[0][0] ?? ""}${parts[1][0] ?? ""}`.toUpperCase();
  }
  return trimmed.slice(0, 2).toUpperCase();
}

function QuickAccessLink({
  to,
  label,
  title,
  Icon,
  className,
  labelClassName,
  onNavigate,
}: {
  to: string;
  label: string;
  title?: string;
  Icon: (typeof QUICK_ACCESS_ITEMS)[number]["Icon"];
  className?: string;
  labelClassName?: string;
  onNavigate?: () => void;
}) {
  const location = useLocation();
  const isActive = isQuickAccessRouteActive(location.pathname, to);

  return (
    <Button asChild variant="ghost" size="touch" className={className}>
      <Link
        to={to}
        aria-label={label}
        aria-current={isActive ? "page" : undefined}
        title={title}
        onClick={onNavigate}
      >
        <Icon />
        <span className={labelClassName}>{label}</span>
      </Link>
    </Button>
  );
}

function SidebarNavItem({
  item,
  onNavigate,
}: {
  item: SidebarItem;
  onNavigate?: () => void;
}) {
  const location = useLocation();
  const hasChildren = !!item.children && item.children.length > 0;
  // Every group is collapsible, so every group LOOKS the same. Two shapes for
  // one structure — a static label here, a chevron button there — read as a
  // bug in the nav rather than a deliberate distinction.
  const startsClosed = item.defaultCollapsed === true;

  const isChildActive = (children: SidebarChildItem[] | undefined) =>
    !!children &&
    children.some((child) => isSidebarRouteActive(location.pathname, child));

  const [isExpanded, setIsExpanded] = useState(() =>
    startsClosed ? isChildActive(item.children) : true
  );
  const isParentActive = hasChildren && isChildActive(item.children);

  useEffect(() => {
    if (isParentActive) setIsExpanded(true);
  }, [isParentActive]);

  const Icon = item.icon;

  if (hasChildren) {
    return (
      <Collapsible open={isExpanded} onOpenChange={setIsExpanded}>
        <CollapsibleTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="touch"
            className="w-full justify-between px-3 font-normal text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900"
            data-active={isParentActive}
          >
            <span className="flex items-center gap-3">
              <Icon className="size-4" />
              {item.label}
            </span>
            {isExpanded ? <ChevronDown /> : <ChevronRight />}
          </Button>
        </CollapsibleTrigger>
        {item.children && (
          <CollapsibleContent className="ml-4 mt-1 space-y-1 border-l border-border pl-3">
            {item.children.map((child) => {
              const childActive = isSidebarRouteActive(
                location.pathname,
                child
              );
              return (
                <Button
                  key={child.to}
                  asChild
                  variant="ghost"
                  size="touch"
                  className={cn(
                    "w-full justify-start px-3 text-sm font-normal",
                    childActive
                      ? "aria-[current=page]:bg-zinc-900 aria-[current=page]:text-white hover:bg-zinc-900 hover:text-white"
                      : "text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900"
                  )}
                >
                  <Link
                    to={child.to}
                    onClick={onNavigate}
                    aria-current={childActive ? "page" : undefined}
                  >
                    {child.label}
                  </Link>
                </Button>
              );
            })}
          </CollapsibleContent>
        )}
      </Collapsible>
    );
  }

  const isActive = isSidebarRouteActive(location.pathname, {
    to: item.to,
    exact: item.to === "/",
  });

  return (
    <Button
      asChild
      variant="ghost"
      size="touch"
      // Admin's grammar: the current page is a SOLID dark row. The inherited
      // `aria-[current=page]` tint was so close to hover that the two were
      // hard to tell apart, which makes a sidebar you have to read rather
      // than glance at.
      className={cn(
        "w-full justify-start gap-3 px-3 font-normal",
        isActive
          ? "aria-[current=page]:bg-zinc-900 aria-[current=page]:text-white hover:bg-zinc-900 hover:text-white"
          : "text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900"
      )}
    >
      <Link
        to={item.to}
        onClick={onNavigate}
        aria-current={isActive ? "page" : undefined}
      >
        <Icon className="size-4" />
        {item.label}
      </Link>
    </Button>
  );
}

function AccountMenu() {
  const { user, logout, isLoggedIn } = useSession();
  const carrierMe = useCarrierMe();
  if (!isLoggedIn) return null;

  const email =
    carrierMe.data?.user.email ?? user?.email ?? user?.name ?? "User";
  const name = carrierMe.data?.user.name ?? user?.name ?? email;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon-touch"
          className="size-11 rounded-full md:size-10"
          aria-label="Account menu"
        >
          <Avatar>
            {user?.picture && <AvatarImage src={user.picture} alt="" />}
            <AvatarFallback className="bg-primary/10 text-xs font-semibold text-primary">
              {avatarInitials(name)}
            </AvatarFallback>
          </Avatar>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel className="font-normal">
          <p className="text-sm font-medium">Signed in as</p>
          <p className="truncate text-xs text-muted-foreground">{email}</p>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem variant="destructive" onSelect={() => logout()}>
          <LogOut className="h-4 w-4" />
          Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function HeaderBrand({ carrierName }: { carrierName: string | null }) {
  const initial = carrierName?.trim().charAt(0).toUpperCase() || "E";
  return (
    <div className="flex min-w-0 items-center gap-2.5">
      <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-primary text-sm font-bold text-primary-foreground">
        {initial}
      </span>
      <div className="hidden min-w-0 leading-tight sm:block">
        <p className="truncate text-sm font-semibold text-foreground">
          {carrierName ?? "Carrier portal"}
        </p>
        <p className="text-[11px] text-muted-foreground">Carrier portal</p>
      </div>
    </div>
  );
}

function SidebarContent({
  groups,
  onNavigate,
}: {
  groups: ReturnType<typeof getVisibleSidebarGroups>;
  onNavigate?: () => void;
}) {
  return (
    <div className="flex h-full min-h-0 flex-col bg-zinc-50">
      <nav className="flex-1 space-y-5 overflow-y-auto p-3">
        {groups.map((group) => (
          <div key={group.label} className="space-y-1">
            <div className="px-3 pb-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-zinc-400">
              {group.label}
            </div>
            {group.items.map((item) => (
              <SidebarNavItem
                key={`${group.label}:${item.to}:${item.label}`}
                item={item}
                onNavigate={onNavigate}
              />
            ))}
          </div>
        ))}
      </nav>
    </div>
  );
}

export function AppLayout() {
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const carrierMe = useCarrierMe();
  const visibleSidebarGroups = getVisibleSidebarGroups(
    carrierMe.data?.capabilities
  );
  // Only personalize the brand when the user belongs to exactly one carrier
  // with a non-empty name; multi-carrier users get the neutral title so we
  // never mislabel the workspace with just the first carrier.
  const carriers = carrierMe.data?.carriers ?? [];
  const singleCarrierName =
    carriers.length === 1 ? carriers[0].name?.trim() || null : null;

  return (
    <ProtectedRoute>
      <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
        <div className="flex h-screen flex-col overflow-hidden bg-background text-foreground">
          <header className="z-20 shrink-0 border-b bg-background">
            {/*
              Same grammar as the Dispatch command bar: one 64px row of 40px
              controls. Dispatch is where a carrier spends the day, so the rest
              of the portal matches it rather than the other way round.
            */}
            <div className="flex h-16 items-center justify-between gap-3 px-3 sm:px-4">
              <div className="flex min-w-0 items-center gap-3">
                <SheetTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    size="icon-touch"
                    className="lg:hidden"
                    aria-label="Open navigation"
                  >
                    <Menu className="h-4 w-4" />
                  </Button>
                </SheetTrigger>
                <HeaderBrand carrierName={singleCarrierName} />
              </div>

              <div className="flex min-w-0 items-center gap-1 sm:gap-2 lg:gap-3">
                <DispatchCenterFilter
                  className="hidden md:flex"
                  triggerClassName="h-10 min-w-[10.5rem] rounded-full bg-background text-[13px] font-semibold lg:data-[size=sm]:h-10"
                />
                {QUICK_ACCESS_ITEMS.map(
                  ({
                    to,
                    label,
                    title,
                    Icon,
                    desktopLabelClassName,
                    visibleOnNarrowMobile,
                  }) => (
                    <QuickAccessLink
                      key={to}
                      to={to}
                      label={label}
                      title={title}
                      Icon={Icon}
                      className={cn(
                        "h-11 min-w-11 gap-1.5 rounded-full px-3 text-[13px] font-semibold md:h-10 lg:min-w-0",
                        !visibleOnNarrowMobile && "hidden sm:inline-flex"
                      )}
                      labelClassName={desktopLabelClassName}
                    />
                  )
                )}
                <Separator
                  orientation="vertical"
                  className="mx-1 hidden h-7 sm:block"
                />
                <AccountMenu />
              </div>
            </div>
          </header>

          <SheetContent
            side="left"
            className="flex w-60 flex-col gap-0 p-0 sm:max-w-60"
          >
            <SheetTitle className="sr-only">Carrier navigation</SheetTitle>
            <SheetDescription className="sr-only">
              Navigate carrier operations and settings.
            </SheetDescription>
            <DispatchCenterFilter
              className="border-b p-3 pr-12 md:hidden"
              triggerClassName="w-full bg-background"
            />
            <nav aria-label="Quick access" className="space-y-1 border-b p-3">
              <p className="px-3 pb-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
                Quick access
              </p>
              {QUICK_ACCESS_ITEMS.map(({ to, label, title, Icon }) => (
                <QuickAccessLink
                  key={to}
                  to={to}
                  label={label}
                  title={title}
                  Icon={Icon}
                  className="w-full justify-start px-3"
                  onNavigate={() => setMobileNavOpen(false)}
                />
              ))}
            </nav>
            <div className="min-h-0 flex-1">
              <SidebarContent
                groups={visibleSidebarGroups}
                onNavigate={() => setMobileNavOpen(false)}
              />
            </div>
          </SheetContent>

          <div className="flex min-h-0 min-w-0 flex-1">
            <aside className="hidden w-56 shrink-0 border-r lg:block">
              <SidebarContent groups={visibleSidebarGroups} />
            </aside>
            <main className="min-w-0 flex-1 overflow-auto">
              <Outlet />
            </main>
          </div>
        </div>
      </Sheet>
    </ProtectedRoute>
  );
}
